/**
 * SweepFreshness — V109-5-2..5-4: the one freshness line every sweep view states (G-8), with the
 * sweep's scope behind a disclosure and "Sweep again" (round 2 Mediums; punch #5).
 *
 *   "Swept today 06:02 · vs yesterday 06:01   Scope ▸            [Sweep again]"
 *
 * The clock is neutral; it turns amber (with a glyph and the word "old") only when the sweep is stale.
 * "Sweep again" is owner / admin (the server enforces it; others get one "Ask an admin" path). At
 * ≤480px it is a quiet inline link-button. Outcomes are announced through the view's live region.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@aigovops/design-system';
import { sourceNameOf, type SweepRunStamp, type SweepSourceStamp } from '@aigovops/core';
import { ApiError } from '../../lib/api';
import { startSweep, sweepKeys } from '../../lib/sweep-api';
import { freshness } from '../../lib/sweep-model';
import { AskAdmin } from '../AskAdmin';
import { CouldntCheckFixes } from '../CouldntCheck';
import type { CouldntItem } from '../../lib/couldnt-check';
import './sweep.css';

const STATUS_WORD: Record<string, string> = {
  ok: 'read', partial: 'read in part', error: "couldn't be read", skipped: 'not read', timed_out: 'timed out', carried: 'read earlier, still current', running: 'reading',
};

export function sourceLabel(s: Pick<SweepSourceStamp, 'source_kind' | 'connector_kind' | 'target'>): string {
  if (s.source_kind === 'repo_scan') return s.target ? `Code · ${s.target}` : 'Code';
  return sourceNameOf(s.connector_kind) ?? 'A connection';
}

/** A source the sweep could not read (or read only in part) as a Couldn't-check item, so its named fix shows inline. */
const failedItem = (s: SweepSourceStamp): CouldntItem => ({
  reason: s.status === 'timed_out' ? 'rate_limited' : 'collector_error', connector_id: s.connector_id, kind: s.connector_kind, system_id: null,
  count: null, resources: [], next_collection_at: null, status: s.status === 'error' ? 'error' : null,
});

export function SweepFreshness({ slug, prefix, run, compared, sources, canSweep, announce, id = 'sweep-fresh' }: {
  slug: string; prefix: string; run: SweepRunStamp | null | undefined; compared: SweepRunStamp | null | undefined; sources: readonly SweepSourceStamp[];
  canSweep: boolean; announce: (m: string) => void; id?: string;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const f = freshness(run, compared);
  // Built-UI (Medium "Partial sweep"): each source the sweep couldn't read, inline, with its fix.
  const failed = sources.filter((s) => s.source_kind !== 'repo_scan' && (s.status === 'error' || s.status === 'partial' || s.status === 'timed_out'));
  const again = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await startSweep(slug);
      announce('Sweep started. The views update when it finishes.');
      await qc.invalidateQueries({ queryKey: sweepKeys.all(slug) });
    } catch (e) {
      const s = e instanceof ApiError ? e.status : 0;
      announce(s === 409 ? 'A sweep is already running.' : s === 429 ? 'You swept less than 15 minutes ago. Try again shortly.' : s === 422 ? 'Nothing is connected to sweep yet.' : 'The sweep could not start.');
    } finally { setBusy(false); }
  };
  return (
    <div className="sw-fresh" data-part="sweep-freshness" data-stale={f.stale ? 'true' : undefined}>
      <p className="sw-fresh-line" id={id}>
        <svg className="sw-clock" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
        <span>{f.text}</span>
        {f.stale ? <span className="sw-stale">{' · '}<span aria-hidden="true">⚠ </span>old</span> : null}
      </p>
      {sources.length ? (
        <details className="sw-scope">
          <summary>Scope</summary>
          <ul aria-label="What the sweep read">
            {sources.map((s, i) => <li key={`${s.connector_id ?? s.target ?? 'src'}-${i}`}><b>{sourceLabel(s)}</b>{s.environment ? ` · ${s.environment}` : ''} · {STATUS_WORD[s.status] ?? s.status}</li>)}
          </ul>
        </details>
      ) : null}
      <span className="sw-fresh-act">
        {canSweep
          ? <Button variant="secondary" compact className="sw-again" aria-disabled={busy || undefined} onClick={() => void again()}>{busy ? 'Starting…' : f.none ? 'Sweep now' : 'Sweep again'}</Button>
          : <AskAdmin slug={slug} compact verb={f.none ? 'run the first sweep' : 'sweep again'} what="starts sweeps" subject="GAC Pro: please run a sweep" />}
      </span>
      {failed.length ? (
        <ul className="sw-failed" aria-label="Sources this sweep couldn't read" data-part="sweep-failed-sources">
          {failed.map((s, i) => (
            <li key={`${s.connector_id ?? 'src'}-${i}`}>
              <span><b>{sourceLabel(s)}</b>{` · ${STATUS_WORD[s.status] ?? s.status}`}</span>
              <CouldntCheckFixes items={[failedItem(s)]} prefix={prefix} compact max={1} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
